import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Input,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { crudeApi } from '../../../helpers/crude_api_helper';
import { formatForDisplay } from '../../../utils/dateformatpickr';

// PC-PPM → Data Tracking. Backend: ChangeRequestController (06.WebApi.Crude),
// per api-contract-pcppm-data-tracking.md ({crudeHost}/v1) — see api-mapping.md
// (06.WebApi.Crude -> `crude` client, not `proHydro`).
//   list GET /v1/ChangeRequest/GetCrudeHistoryList (paginated)
// Structurally identical to GetMasterDataHistoryList (api-contract-data-tracking.md)
// but server-filtered to EntityName == "Crude" (Registration/Assay commands)
// instead of excluding it — don't confuse the two endpoints.
// Read-only: no create/update/delete/approve/reject for this menu. Rows still
// awaiting review/approve are excluded server-side; `details[]`
// (Field/Before/After) is already included per row — no extra request needed
// for the expand.
// `sortField` must be PascalCase; NO server whitelist here (unlike
// GetMasterDataHistoryList) — any response property works, including `Status`
// (deliberately left sortable per the contract's requirement note). `Details`
// still can't be a sortField (list property, not scalar).
// `searchText` is narrower than GetMasterDataHistoryList: only matches
// createdBy/entityName/dataKey/changedType (the 4 visible text columns) —
// server-side scope, nothing to change here.
const ENDPOINT = '/v1/ChangeRequest/GetCrudeHistoryList';

const toPascalCase = field =>
  field ? field.charAt(0).toUpperCase() + field.slice(1) : field;

// `fieldName` may be formatted "Group→Field" for UI grouping (per BE note);
// split it so the expand table shows a clean field label.
const splitFieldName = fieldName => {
  const raw = fieldName || '';
  const parts = raw.split('→');
  return parts.length > 1
    ? { group: parts[0], field: parts.slice(1).join('→') }
    : { group: null, field: raw };
};

const ExpandedFieldChanges = ({ data }) => (
  <div className='p-3 bg-light'>
    <table className='table table-sm mb-0'>
      <thead>
        <tr>
          <th>Field</th>
          <th>Before</th>
          <th>After</th>
        </tr>
      </thead>
      <tbody>
        {(data.details || []).map((change, i) => {
          const { group, field } = splitFieldName(change.fieldName);
          return (
            <tr key={i}>
              <td>{group ? `${group} / ${field}` : field}</td>
              <td>{change.oldValue || '-'}</td>
              <td>{change.newValue || '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const PcPpmDataTrackingView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  document.title = 'PC-PPM Data Tracking';

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchKeyword.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await crudeApi.get(ENDPOINT, {
        page,
        pageSize,
        searchText: debouncedSearch || undefined,
        sortField: sortField || undefined,
        sortOrder: sortField ? sortOrder : undefined,
      });
      setItems(Array.isArray(result?.items) ? result.items : []);
      setTotalCount(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sortField, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Server-side sort: only fires for columns that carry a `sortField`
  // (Total Changes/`details` isn't sortable server-side, per contract).
  const handleSort = (column, sortDirection) => {
    if (!column?.id) return;
    setSortField(toPascalCase(column.id));
    setSortOrder(sortDirection === 'desc' ? 'Desc' : 'Asc');
    setPage(1);
  };

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const columns = [
    {
      id: 'createdBy',
      name: headerLabel('Created'),
      selector: row => row.createdBy,
      sortable: true,
      wrap: true,
    },
    {
      id: 'created',
      name: headerLabel('Created At'),
      selector: row => formatForDisplay(row.created),
      sortable: true,
      wrap: true,
    },
    {
      id: 'entityName',
      name: headerLabel('Entity Name'),
      selector: row => row.entityName,
      sortable: true,
      wrap: true,
    },
    {
      id: 'dataKey',
      name: headerLabel('Crude Name'),
      selector: row => row.dataKey,
      sortable: true,
      wrap: true,
    },
    {
      id: 'changedType',
      name: headerLabel('Action'),
      selector: row => row.changedType,
      sortable: true,
      wrap: true,
    },
    {
      name: headerLabel('Total Changes'),
      selector: row => (row.details || []).length,
      center: true,
      width: '140px',
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='PC-PPM Data Tracking' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='PcPpmDataTrackingList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      PC-PPM Data Tracking
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='search-box'>
                        <Input
                          type='text'
                          className='form-control form-control-sm'
                          placeholder='Search...'
                          value={searchKeyword}
                          onChange={e => setSearchKeyword(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      progressPending={loading}
                      pagination
                      paginationServer
                      paginationTotalRows={totalCount}
                      paginationDefaultPage={page}
                      paginationPerPage={pageSize}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={p => setPage(p)}
                      onChangeRowsPerPage={(newPerPage, p) => {
                        setPageSize(newPerPage);
                        setPage(p);
                      }}
                      sortServer
                      onSort={handleSort}
                      expandableRows
                      expandableRowsComponent={ExpandedFieldChanges}
                      wrap
                      dense
                      striped
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No data tracking entries found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PcPpmDataTrackingView;
